/**
 * 웹 ↔ RN WebView 브릿지
 * - RN으로 메시지 보내기: sendToRN(type, payload)
 * - RN에서 메시지 받기: onRNMessage(type, callback)
 */

const isInWebView = () => !!window.ReactNativeWebView;

/** RN으로 메시지 전송 */
export const sendToRN = (type, payload = {}) => {
  if (!isInWebView()) return false;
  try {
    window.ReactNativeWebView.postMessage(JSON.stringify({ type, payload }));
    return true;
  } catch (e) {
    console.warn('[webBridge] sendToRN error:', e);
    return false;
  }
};

/** RN에서 오는 메시지 리스너 등록 */
const listeners = {};

export const onRNMessage = (type, callback) => {
  if (!listeners[type]) listeners[type] = [];
  listeners[type].push(callback);

  // cleanup 함수 반환
  return () => {
    listeners[type] = listeners[type].filter((cb) => cb !== callback);
  };
};

/** RN 메시지 수신 처리 (앱 초기화 시 1회 호출) */
export const initBridgeListener = () => {
  const handler = (event) => {
    let data;
    try {
      data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
    } catch {
      return;
    }

    const type = data?.type;
    if (!type) return;

    const cbs = listeners[type] || [];
    cbs.forEach((cb) => {
      try { cb(data.payload || {}); } catch (e) {
        console.warn('[webBridge] listener error:', type, e);
      }
    });
  };

  window.addEventListener('message', handler);
  document.addEventListener('message', handler); // Android WebView 호환

  return () => {
    window.removeEventListener('message', handler);
    document.removeEventListener('message', handler);
  };
};

/** WEB_READY 신호 전송 (재시도 포함) */
export const sendWebReady = () => {
  const tryReady = (attempt) => {
    if (attempt > 20) return; // 10초 후 포기
    if (sendToRN('WEB_READY', { at: Date.now() })) return;
    setTimeout(() => tryReady(attempt + 1), 500);
  };
  tryReady(0);
};

/** 네이티브 공유 요청 */
export const requestShare = ({ title, text, url }) => {
  return sendToRN('SHARE', { title, text, url });
};

/** 주소록 요청 */
export const requestContacts = () => {
  return sendToRN('OPEN_CONTACTS', {});
};

/** 앱인지 확인 */
export { isInWebView };

/**
 * 네이티브 소셜 로그인 요청 (Promise 패턴)
 * - RN 으로 START_SIGNIN 전송 → authDispatcher.handleStartSignin 실행
 * - SIGNIN_RESULT 메시지 한 번 받으면 resolve
 * - timeoutMs 내 응답 없으면 reject
 */
export const requestNativeSocialSignIn = (provider, timeoutMs = 60000) => {
  return new Promise((resolve, reject) => {
    if (!isInWebView()) {
      reject(new Error('RN WebView 환경 아님'));
      return;
    }
    let done = false;
    const off = onRNMessage('SIGNIN_RESULT', (payload) => {
      if (done) return;
      if (!payload || payload.provider !== provider) return;
      done = true;
      off();
      if (payload.success === false) {
        const err = new Error(payload.error_message || payload.error_code || '네이티브 로그인 실패');
        err.code = payload.error_code || 'native/signin-failed';
        reject(err);
        return;
      }
      resolve(payload);
    });
    sendToRN('START_SIGNIN', { provider });
    setTimeout(() => {
      if (done) return;
      done = true;
      off();
      reject(new Error('네이티브 로그인 응답 시간 초과'));
    }, timeoutMs);
  });
};
